import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { client } from "@/lib/api";

/**
 * Wave 15 Step 9 (Founder 2 Oct 2026) · notifications panel · the dropdown
 * that opens under the heart. Phone: full width, 12px side margins over a
 * dim scrim. Desktop: 380px panel anchored under the heart.
 * Messages NEVER appear here · they live on the squad dot.
 */
export default function NotificationsPanel({ open, onClose, items, refresh, markOneReadNow, markAllReadNow, isDesktop, anchorRight = 16 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onClose]);
  if (!open) return null;

  // Wave C bug O (Vania 7 Oct) · "mark all read" now persists server-side
  // via the shared hook so the next poll won't un-dismiss the dot.
  const markAll = async () => {
    if (markAllReadNow) await markAllReadNow();
    else { try { await client.post("/notifications/mark-all-read"); refresh(); } catch {} }
  };

  const PanelInner = (
    <div ref={ref} data-testid="notifications-panel"
         style={{ background: "var(--ww-ground)", color: "var(--ww-text)",
                  border: "1px solid var(--ww-line)", borderRadius: 16,
                  overflow: "hidden",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
                  maxHeight: isDesktop ? 520 : "70dvh",
                  display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "12px 14px", borderBottom: "1px solid var(--ww-line)" }}>
        <div style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 15,
                       textTransform: "lowercase" }}>notifications</div>
        <button onClick={markAll} data-testid="notifications-mark-all"
                style={{ background: "transparent", border: 0, cursor: "pointer",
                         color: "var(--ww-text-muted)", fontFamily: "Arial",
                         fontSize: 12, textTransform: "lowercase" }}>
          mark all read
        </button>
      </div>
      <div style={{ flex: 1, overflowY: "auto" }}>
        {items === null ? (
          <p style={{ padding: 14, fontFamily: "Arial", fontSize: 13,
                       color: "var(--ww-text-muted)" }}>loading…</p>
        ) : items.length === 0 ? (
          <p style={{ padding: 24, textAlign: "center", fontFamily: "Arial",
                       fontSize: 13, color: "var(--ww-text-muted)" }}>
            nothing new yet. keep saving events · activity shows up here.
          </p>
        ) : (
          <ul data-testid="notifications-list">
            {items.slice(0, 10).map((n) => <NotifRow key={n.id} n={n} onClose={onClose} markOneReadNow={markOneReadNow} />)}
          </ul>
        )}
      </div>
      <Link to="/app/notifications" onClick={onClose}
            data-testid="notifications-see-all"
            style={{ padding: "10px 14px", borderTop: "1px solid var(--ww-line)",
                     textAlign: "center", textDecoration: "none",
                     color: "var(--ww-text)", fontFamily: "Poppins",
                     fontWeight: 700, fontSize: 13, textTransform: "lowercase" }}>
        see all
      </Link>
    </div>
  );

  if (isDesktop) {
    return (
      <div style={{ position: "absolute", top: 60, right: anchorRight,
                     width: 380, zIndex: 50 }}>
        {PanelInner}
      </div>
    );
  }
  return (
    <div onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
         style={{ position: "fixed", inset: 0, zIndex: 60,
                   background: "rgba(0,0,0,0.4)",
                   paddingTop: 64, paddingLeft: 12, paddingRight: 12 }}>
      {PanelInner}
    </div>
  );
}

function NotifRow({ n, onClose, markOneReadNow }) {
  const copy = renderCopy(n);
  const href = renderHref(n);
  const actor = n.actor;
  const avatarUrl = actor?.picture || actor?.photo_url || "";
  const cover = n.event?.poster_url || n.event?.cover_image_url || "";

  // Wave C bug O (Vania 7 Oct) · clicking a row persists read server-side so
  // the next poll can't bring back the dot. Fire-and-forget before nav.
  const onClick = () => {
    if (!n.read_at && markOneReadNow) { markOneReadNow(n.id); }
    onClose();
  };

  return (
    <li data-testid={`notif-${n.type}-${n.id}`} data-read={n.read_at ? "1" : "0"}
        style={{ borderBottom: "1px solid var(--ww-line)" }}>
      <Link to={href} onClick={onClick}
            data-testid={`notif-row-${n.id}`}
            style={{ display: "flex", alignItems: "center", gap: 10,
                     padding: "10px 14px", textDecoration: "none",
                     color: "var(--ww-text)" }}>
        {cover ? (
          <img src={cover} alt="" style={{ width: 40, height: 40, borderRadius: 10, objectFit: "cover" }} />
        ) : avatarUrl ? (
          <img src={avatarUrl} alt="" style={{ width: 40, height: 40, borderRadius: 999, objectFit: "cover" }} />
        ) : (
          <span style={{ width: 40, height: 40, borderRadius: 999,
                          background: "var(--ww-sunken)",
                          border: "1px solid var(--ww-line)",
                          display: "grid", placeItems: "center",
                          color: "var(--ww-text)", fontFamily: "Poppins",
                          fontWeight: 700, fontSize: 15 }}>
            {(actor?.handle || "?").slice(0, 1).toUpperCase()}
          </span>
        )}
        <div style={{ flex: 1, minWidth: 0, fontFamily: "Arial", fontSize: 13,
                       color: "var(--ww-text)", overflow: "hidden",
                       textOverflow: "ellipsis" }}>
          {copy}
        </div>
        {!n.read_at && (
          <span data-testid={`notif-unread-${n.id}`}
                style={{ width: 9, height: 9, borderRadius: 999,
                         background: "#FF6B9D", flexShrink: 0 }} />
        )}
      </Link>
    </li>
  );
}

export function renderCopy(n) {
  const who = n.actor?.handle ? "@" + n.actor.handle : "someone";
  switch (n.type) {
    case "saved_event":
      return `you saved ${n.event?.title ? n.event.title.toLowerCase() : "an event"}`;
    case "followed":
      return `${who} started following you`;
    case "squad_added":
      return `${who} added you to ${n.squad?.name ? n.squad.name.toLowerCase() : "a squad"}`;
    case "squad_invite_accepted":
      return `${who} joined ${n.squad?.name ? n.squad.name.toLowerCase() : "the squad"}`;
    case "liked_post":
      return `${who} liked your photo`;
    case "commented":
      return `${who} replied`;
    default:
      return "activity";
  }
}

export function renderHref(n) {
  if (n.target_kind === "event" && n.target_id) return `/app/event/${n.target_id}`;
  if (n.target_kind === "squad" && n.target_id) return `/app/squad/${n.target_id}`;
  if (n.target_kind === "user" && n.actor?.handle) return `/app/people/${n.actor.handle}`;
  return "/app/notifications";
}
